import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Controller,
  useForm,
  type FieldError,
  type FieldErrors,
  type UseFormSetError,
} from 'react-hook-form';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useDebouncedValue } from '@mantine/hooks';
import type { AxiosError } from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  ActionIcon,
  Box,
  Group,
  Loader,
  Paper,
  rem,
  ScrollArea,
  Stack,
  Stepper,
  Text,
  UnstyledButton,
} from '@mantine/core';
import { IoClose } from 'react-icons/io5';
import {
  Alert,
  Button,
  ControlledCheckbox,
  ControlledInput,
  ControlledSelect,
  ControlledTextarea,
  DoubleCheckModal,
  Input,
  Radio,
} from '@shared/ui';
import {
  SELLER_ME_QUERY_KEY,
  sellerOnboardingApi,
  type BankSuggestOption,
  type PartySuggestOption,
  type SellerAccountType,
  type SellerApplication,
  type SellerApplicationUpdate,
  type SellerTaxSystem,
} from '@modules/seller-onboarding/api';
import { authApi } from '@modules/auth/api/auth.api';
import { sellerNeedsRevisionRegistrationWizard } from '@modules/seller-onboarding/moderation-revision';
import { authUtils } from '@shared/utils/axios-instance';
import { clearSellerClientState } from '@shared/utils/clear-seller-client-state';
import { devProxyS3Url } from '@shared/utils/dev-proxy-s3-url';
import { openSellerDocument } from '@modules/seller-onboarding/open-seller-document';
import {
  BANK_ACCOUNT_CHECKSUM_ERROR,
  isBankAccountChecksumValid,
} from '@shared/utils/bank-account-checksum';
import { fileLabelFromUrl } from '@shared/utils/file-label-from-url';
import { formatPickupPhoneForInput, toApiPhone } from '@modules/pickup-points/utils/formatPickupPhone';
import logoMark from '../../assets/logo-text-empty.png';
import './SellerRegisterPage.css';

/** Шагов в мастере (индексы Stepper 0..STEP_COUNT-1). currentStep в API = индекс + 1 (1..STEP_COUNT). */
const STEP_COUNT = 4;
const LAST_STEP_INDEX = STEP_COUNT - 1;

/** Как в DaData STATUS_LABELS.ACTIVE → подстановка в заявку из lookup */
const EGRUL_STATUS_ACTIVE_LABEL = 'Действует';

const ACCOUNT_LABELS: Record<SellerAccountType, string> = {
  LEGAL_ENTITY: 'Юридическое лицо (ООО / АО)',
  INDIVIDUAL_ENTREPRENEUR: 'Индивидуальный предприниматель (ИП)',
  SELF_EMPLOYED: 'Самозанятый',
};

/** Временно скрыто в UI; логика SELF_EMPLOYED в форме/API сохранена. */
const SELF_EMPLOYED_REGISTRATION_ENABLED = false;

/** Временно скрыто в UI; поле/API/валидация сохранены. */
const TAX_SYSTEM_SELECTION_ENABLED = false;

/** Только цифры, не больше `maxLen` (расчётный счёт — 20). */
function digitsOnlyMax(value: string, maxLen: number): string {
  return value.replace(/\D/g, '').slice(0, maxLen);
}

/** ИНН/ОГРН — с 9 цифр. Название — с 5 символов (как на бэке, party-suggest-query.util). */
function isPartySuggestQueryReady(query: string): boolean {
  const q = query.trim();
  if (!q) return false;
  const min = /^\d+$/.test(q) ? 9 : 5;
  return q.length >= min;
}

/** Варианты налоговой системы на шаге «Тип» (значения API — см. SellerTaxSystem). */
function taxOptionsForAccountType(
  accountType: SellerAccountType | '',
): { value: SellerTaxSystem; label: string }[] {
  if (accountType === 'LEGAL_ENTITY') {
    return [
      { value: 'OSNO', label: 'С НДС' },
      { value: 'USN_INCOME', label: 'Без НДС' },
    ];
  }
  if (accountType === 'INDIVIDUAL_ENTREPRENEUR') {
    return [
      { value: 'OSNO', label: 'С НДС' },
      { value: 'USN_INCOME', label: 'Без НДС' },
      { value: 'NPD', label: 'НПД' },
    ];
  }
  return [];
}

export interface SellerRegisterFormValues {
  accountType: SellerAccountType | '';
  selfEmployedAnnualTurnoverLimitRub: number | undefined;
  inn: string;
  kpp: string;
  ogrn: string;
  companyName: string;
  legalAddress: string;
  companyStatusText: string;
  bankAccountNumber: string;
  correspondentAccount: string;
  bik: string;
  bankName: string;
  taxSystem: SellerTaxSystem | null;
  directorFullName: string;
  contactPhone: string;
  contactEmail: string;
  egrulExtractFileUrls: string[];
  passportScanFileUrls: string[];
  logoFileUrl: string;
  shopName: string;
  activityDescription: string;
  offerAccepted: boolean;
  /** Клиентский флаг: данные организации получены после выбора из подсказок DaData (ЮЛ / ИП) */
  companyLookupApplied: boolean;
  /** Только для ошибки submit: PDF оферты не сформирован */
  offerPdfSubmitError?: string;
}

type SellerPatchFieldErrors = Partial<Record<keyof SellerRegisterFormValues, string>>;

function extractPatchFieldErrors(error: unknown): SellerPatchFieldErrors {
  const ax = error as AxiosError<{
    fieldErrors?: Record<string, string | undefined>;
  }>;
  const source = ax.response?.data?.fieldErrors;
  if (!source || typeof source !== 'object') {
    return {};
  }
  const out: SellerPatchFieldErrors = {};
  for (const [key, message] of Object.entries(source)) {
    if (typeof message !== 'string' || message.trim().length === 0) continue;
    if (key === 'email') {
      out.contactEmail = message;
      continue;
    }
    if (key in EMPTY_FORM) {
      out[key as keyof SellerRegisterFormValues] = message;
    }
  }
  return out;
}

const EMPTY_FORM: SellerRegisterFormValues = {
  accountType: '',
  selfEmployedAnnualTurnoverLimitRub: undefined,
  inn: '',
  kpp: '',
  ogrn: '',
  companyName: '',
  legalAddress: '',
  companyStatusText: '',
  bankAccountNumber: '',
  correspondentAccount: '',
  bik: '',
  bankName: '',
  taxSystem: null,
  directorFullName: '',
  contactPhone: '',
  contactEmail: '',
  egrulExtractFileUrls: [],
  passportScanFileUrls: [],
  logoFileUrl: '',
  shopName: '',
  activityDescription: '',
  offerAccepted: false,
  companyLookupApplied: false,
  offerPdfSubmitError: undefined,
};

function appToFormValues(app: SellerApplication): SellerRegisterFormValues {
  return {
    accountType: (app.accountType ?? '') as SellerAccountType | '',
    selfEmployedAnnualTurnoverLimitRub: app.selfEmployedAnnualTurnoverLimitRub
      ? Number(app.selfEmployedAnnualTurnoverLimitRub)
      : undefined,
    inn: app.inn ?? '',
    kpp: app.kpp ?? '',
    ogrn: app.ogrn ?? '',
    companyName: app.companyName ?? '',
    legalAddress: app.legalAddress ?? '',
    companyStatusText: app.companyStatusText ?? '',
    bankAccountNumber: digitsOnlyMax(app.bankAccountNumber ?? '', 20),
    correspondentAccount: app.correspondentAccount ?? '',
    bik: app.bik ?? '',
    bankName: app.bankName ?? '',
    taxSystem:
      app.taxSystem === 'USN_INCOME_EXPENSE'
        ? 'USN_INCOME'
        : (app.taxSystem ?? null),
    directorFullName: app.directorFullName ?? '',
    // Телефон директора — только из заявки. Не подставляем User.phone:
    // при входе по email у аккаунта может быть старый телефон, это не контакт директора.
    contactPhone: formatPickupPhoneForInput((app.contactPhone ?? '').trim()),
    // Email директора — только из заявки, не из email входа (User.email).
    contactEmail: (app.contactEmail ?? '').trim(),
    egrulExtractFileUrls: [...(app.egrulExtractFileUrls ?? [])],
    passportScanFileUrls: [...(app.passportScanFileUrls ?? [])],
    logoFileUrl: app.logoFileUrl ?? '',
    shopName: app.shopName ?? '',
    activityDescription: app.activityDescription ?? '',
    offerAccepted: app.offerAccepted,
    companyLookupApplied:
      app.accountType === 'SELF_EMPLOYED' || !app.accountType
        ? false
        : Boolean((app.ogrn ?? '').trim()),
    offerPdfSubmitError: undefined,
  };
}

function optionalStr(s: string | undefined | null): string | undefined {
  const t = (s ?? '').trim();
  return t === '' ? undefined : t;
}

function valuesToApiPatch(v: SellerRegisterFormValues): SellerApplicationUpdate {
  return {
    accountType: v.accountType ? v.accountType : undefined,
    selfEmployedAnnualTurnoverLimitRub: v.selfEmployedAnnualTurnoverLimitRub,
    inn: optionalStr(v.inn),
    kpp: optionalStr(v.kpp),
    ogrn: optionalStr(v.ogrn),
    companyName: optionalStr(v.companyName),
    legalAddress: optionalStr(v.legalAddress),
    companyStatusText: optionalStr(v.companyStatusText),
    bankAccountNumber: optionalStr(digitsOnlyMax(v.bankAccountNumber ?? '', 20)),
    correspondentAccount: optionalStr(v.correspondentAccount),
    bik: optionalStr(v.bik),
    bankName: optionalStr(v.bankName),
    taxSystem: v.taxSystem ?? undefined,
    directorFullName: optionalStr(v.directorFullName),
    contactPhone: optionalStr(toApiPhone(v.contactPhone ?? '')),
    contactEmail: optionalStr(v.contactEmail),
    shopName: optionalStr(v.shopName),
    activityDescription: optionalStr(v.activityDescription),
    offerAccepted: v.offerAccepted,
  };
}

/** Ошибки полей не с шага «Документы» — показываем сводкой вверху шага */
const PREVIOUS_STEPS_SUBMIT_ERROR_KEYS: (keyof SellerRegisterFormValues)[] = [
  'accountType',
  'inn',
  'companyName',
  'legalAddress',
  'bankAccountNumber',
  'correspondentAccount',
  'bik',
  'bankName',
  'taxSystem',
  'kpp',
  'directorFullName',
  'companyStatusText',
  'contactPhone',
  'contactEmail',
];

function collectSubmitErrorMessages(
  errs: FieldErrors<SellerRegisterFormValues>,
  keys: (keyof SellerRegisterFormValues)[],
): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const key of keys) {
    const msg = errs[key]?.message;
    if (typeof msg === 'string' && msg.length > 0 && !seen.has(msg)) {
      seen.add(msg);
      out.push(msg);
    }
  }
  return out;
}

/**
 * Клиентская проверка перед submit — соответствует be assertReadyForSubmit.
 * Возвращает false, если выставлены setError; иначе true.
 */
function validateReadyForSubmit(
  v: SellerRegisterFormValues,
  app: SellerApplication,
  setError: UseFormSetError<SellerRegisterFormValues>,
): boolean {
  let ok = true;
  const fail = (name: Parameters<UseFormSetError<SellerRegisterFormValues>>[0], message: string) => {
    ok = false;
    setError(name, { type: 'manual', message });
  };
  const req = (name: Parameters<UseFormSetError<SellerRegisterFormValues>>[0], message: string) => {
    fail(name, message);
  };

  if (!v.accountType) {
    req('accountType', 'Заполните поле: тип аккаунта');
  }
  if (!(v.inn ?? '').trim()) {
    req('inn', 'Заполните поле: ИНН');
  }
  const hasSellerDisplayName = Boolean((v.companyName ?? '').trim());
  if (!hasSellerDisplayName) {
    req(
      'companyName',
      v.accountType === 'SELF_EMPLOYED'
        ? 'Заполните поле: ФИО полностью (шаг «Компания»)'
        : 'Заполните поле: название компании',
    );
  }
  if (!(v.legalAddress ?? '').trim()) {
    req('legalAddress', 'Заполните поле: юридический адрес');
  }
  if (!(v.bankAccountNumber ?? '').trim()) {
    req('bankAccountNumber', 'Заполните поле: расчётный счёт');
  }
  if (!(v.correspondentAccount ?? '').trim()) {
    req('correspondentAccount', 'Заполните поле: корр. счёт');
  }
  if (!(v.bik ?? '').trim()) {
    req('bik', 'Заполните поле: БИК');
  }
  if (isBankAccountChecksumValid(v.bik, v.bankAccountNumber) === false) {
    fail('bankAccountNumber', BANK_ACCOUNT_CHECKSUM_ERROR);
  }
  if (!(v.bankName ?? '').trim()) {
    req('bankName', 'Заполните поле: название банка');
  }
  if (TAX_SYSTEM_SELECTION_ENABLED && !v.taxSystem) {
    req('taxSystem', 'Заполните поле: система налогообложения');
  }

  if (v.accountType === 'LEGAL_ENTITY' && !(v.kpp ?? '').trim()) {
    req('kpp', 'Заполните поле: КПП (для юрлица)');
  }

  if (TAX_SYSTEM_SELECTION_ENABLED && v.accountType === 'SELF_EMPLOYED') {
    if (v.taxSystem !== 'NPD') {
      fail('taxSystem', 'Для самозанятого выберите систему налогообложения НПД');
    }
  }

  if (v.accountType && v.accountType !== 'SELF_EMPLOYED' && !(v.directorFullName ?? '').trim()) {
    req('directorFullName', 'Заполните поле: ФИО руководителя / ИП');
  }

  if (v.accountType === 'LEGAL_ENTITY' || v.accountType === 'INDIVIDUAL_ENTREPRENEUR') {
    const st = (v.companyStatusText ?? '').trim();
    if (st && st !== EGRUL_STATUS_ACTIVE_LABEL) {
      fail(
        'companyStatusText',
        `По данным ЕГРЮЛ организация не в статусе «${EGRUL_STATUS_ACTIVE_LABEL}» (указано: ${st}).`,
      );
    }
  }

  if (!(v.contactPhone ?? '').trim()) {
    req('contactPhone', 'Заполните поле: телефон директора');
  }
  if (!(v.contactEmail ?? '').trim()) {
    req('contactEmail', 'Заполните поле: email директора');
  }

  if (v.accountType && v.accountType !== 'SELF_EMPLOYED' && (v.egrulExtractFileUrls?.length ?? 0) === 0) {
    req('egrulExtractFileUrls', 'Загрузите выписку ЕГРЮЛ / ЕГРИП');
  }
  if ((v.passportScanFileUrls?.length ?? 0) === 0) {
    req('passportScanFileUrls', 'Загрузите скан паспорта');
  }
  if (!(app.offerPdfFileUrl ?? '').trim()) {
    fail(
      'offerPdfSubmitError',
      'Сформируйте и ознакомьтесь с PDF договора-оферты на шаге «Документы»',
    );
  }
  if (!(v.logoFileUrl ?? '').trim()) {
    req('logoFileUrl', 'Загрузите логотип магазина');
  }
  if (!(v.shopName ?? '').trim()) {
    req('shopName', 'Заполните поле: название магазина');
  }
  if (!(v.activityDescription ?? '').trim()) {
    req('activityDescription', 'Заполните поле: описание деятельности');
  }

  return ok;
}

export default function SellerRegisterPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [active, setActive] = useState(0);
  const [innLookupLoading, setInnLookupLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [offerPdfLoading, setOfferPdfLoading] = useState(false);
  const [offerPdfError, setOfferPdfError] = useState<string | null>(null);

  const {
    control,
    handleSubmit,
    reset,
    getValues,
    setValue,
    watch,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<SellerRegisterFormValues>({
    mode: 'onChange',
    defaultValues: EMPTY_FORM,
  });

  const accountType = watch('accountType');
  const isBusinessAccount =
    accountType === 'LEGAL_ENTITY' || accountType === 'INDIVIDUAL_ENTREPRENEUR';
  const taxSystem = watch('taxSystem');
  const egrulExtractFileUrls = watch('egrulExtractFileUrls');
  const passportScanFileUrls = watch('passportScanFileUrls');
  const logoFileUrl = watch('logoFileUrl');
  const innQueryForSuggest = watch('inn');
  const companyStatusText = watch('companyStatusText');
  const companyLookupApplied = watch('companyLookupApplied');
  const offerAccepted = watch('offerAccepted');
  const previousStepsSubmitMessages = useMemo(
    () => collectSubmitErrorMessages(errors, PREVIOUS_STEPS_SUBMIT_ERROR_KEYS),
    [errors],
  );
  const submitApiError = (errors as { root?: { submit?: FieldError } }).root?.submit;
  const [debouncedInnSuggest] = useDebouncedValue(innQueryForSuggest ?? '', 380);
  const [partySuggestions, setPartySuggestions] = useState<PartySuggestOption[]>([]);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [bankSearchQuery, setBankSearchQuery] = useState('');
  const [debouncedBankQuery] = useDebouncedValue(bankSearchQuery, 380);
  const [bankSuggestions, setBankSuggestions] = useState<BankSuggestOption[]>([]);
  const [bankSuggestLoading, setBankSuggestLoading] = useState(false);
  /** Какой блок документов сейчас грузится на S3 (лоадер в кнопке «Выбрать»). */
  const [docUploadKind, setDocUploadKind] = useState<'egrul' | 'passport' | 'logo' | null>(null);
  const docUploadLocksInputs = docUploadKind !== null;
  /** Не запрашиваем suggest/bank после гидрации с сервера; только после правок в поле (и сбрасываем после выбора из списка). */
  const allowBankSuggestFetch = useRef(false);
  /** То же для suggest/party по ИНН на шаге «Компания». */
  const allowPartySuggestFetch = useRef(false);

  useEffect(() => {
    if (accountType === 'SELF_EMPLOYED' && taxSystem !== 'NPD') {
      setValue('taxSystem', 'NPD');
    }
    if (accountType === 'LEGAL_ENTITY' && taxSystem === 'NPD') {
      setValue('taxSystem', null);
    }
  }, [accountType, taxSystem, setValue]);

  useEffect(() => {
    if (accountType === 'SELF_EMPLOYED') {
      setPartySuggestions([]);
      return;
    }
    const q = debouncedInnSuggest.trim();
    if (!isPartySuggestQueryReady(q)) {
      setPartySuggestions([]);
      return;
    }
    if (!allowPartySuggestFetch.current) {
      setPartySuggestions([]);
      return;
    }
    let cancelled = false;
    const partyTypeForDaData =
      accountType === 'LEGAL_ENTITY'
        ? ('LEGAL' as const)
        : accountType === 'INDIVIDUAL_ENTREPRENEUR'
          ? ('INDIVIDUAL' as const)
          : undefined;

    setSuggestLoading(true);
    sellerOnboardingApi
      .suggestParty({
        query: q,
        ...(partyTypeForDaData ? { partyType: partyTypeForDaData } : {}),
      })
      .then((r) => {
        if (cancelled || !allowPartySuggestFetch.current) return;
        setPartySuggestions(r.suggestions);
      })
      .catch(() => {
        if (!cancelled && allowPartySuggestFetch.current) setPartySuggestions([]);
      })
      .finally(() => {
        if (!cancelled) setSuggestLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedInnSuggest, accountType]);

  useEffect(() => {
    const q = debouncedBankQuery.trim();
    if (q.length < 2) {
      setBankSuggestions([]);
      return;
    }
    if (!allowBankSuggestFetch.current) {
      setBankSuggestions([]);
      return;
    }
    let cancelled = false;
    setBankSuggestLoading(true);
    sellerOnboardingApi
      .suggestBank({ query: q })
      .then((r) => {
        if (cancelled || !allowBankSuggestFetch.current) return;
        setBankSuggestions(r.suggestions);
      })
      .catch(() => {
        if (!cancelled && allowBankSuggestFetch.current) setBankSuggestions([]);
      })
      .finally(() => {
        if (!cancelled) setBankSuggestLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedBankQuery]);

  const { data: app, isLoading } = useQuery({
    queryKey: SELLER_ME_QUERY_KEY,
    queryFn: () => sellerOnboardingApi.getMe(),
  });

  /** В режиме доработки один раз открываем последний шаг; дальше active не перетираем sync с API. */
  const revisionStartAppliedRef = useRef(false);

  useEffect(() => {
    if (!app) return;
    reset(appToFormValues(app), { keepDirtyValues: true });
    allowBankSuggestFetch.current = false;
    allowPartySuggestFetch.current = false;
    setBankSearchQuery(app.bankName?.trim() ?? '');

    const isRevision = sellerNeedsRevisionRegistrationWizard(
      app.status,
      app.moderationRejectionReason,
      app.moderationRejectionKind,
    );
    if (isRevision) {
      if (!revisionStartAppliedRef.current) {
        revisionStartAppliedRef.current = true;
        setActive(LAST_STEP_INDEX);
      }
      return;
    }

    revisionStartAppliedRef.current = false;
    const cs = app.currentStep ?? 1;
    setActive(Math.max(0, Math.min(LAST_STEP_INDEX, cs - 1)));
  }, [app?.id, app?.updatedAt, app, reset]);

  /** На шаге «Документы» генерируем PDF с реквизитами, сохраняем в S3; при перегенерации снимаем галочку согласия на клиенте. */
  useEffect(() => {
    if (active !== LAST_STEP_INDEX) return;
    let cancelled = false;
    setOfferPdfLoading(true);
    setOfferPdfError(null);
    void sellerOnboardingApi
      .generateOfferPdf()
      .then((next) => {
        if (cancelled) return;
        queryClient.setQueryData(SELLER_ME_QUERY_KEY, (prev: SellerApplication | undefined) => ({
          ...(prev ?? next),
          ...next,
          user: next.user ?? prev?.user ?? null,
        }));
        setValue('offerAccepted', next.offerAccepted);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        const ax = e as {
          response?: { data?: { message?: string | string[] } };
        };
        const raw = ax.response?.data?.message;
        const msg = Array.isArray(raw)
          ? raw.join(' ')
          : raw ?? (e instanceof Error ? e.message : null);
        setOfferPdfError(msg || 'Не удалось сформировать PDF');
      })
      .finally(() => {
        if (!cancelled) setOfferPdfLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [active, queryClient, setValue]);

  const regenerateOfferPdf = useCallback(async () => {
    setOfferPdfLoading(true);
    setOfferPdfError(null);
    try {
      const next = await sellerOnboardingApi.generateOfferPdf();
      queryClient.setQueryData(SELLER_ME_QUERY_KEY, (prev: SellerApplication | undefined) => ({
        ...(prev ?? next),
        ...next,
        user: next.user ?? prev?.user ?? null,
      }));
      setValue('offerAccepted', next.offerAccepted);
    } catch (e: unknown) {
      const ax = e as {
        response?: { data?: { message?: string | string[] } };
      };
      const raw = ax.response?.data?.message;
      const msg = Array.isArray(raw)
        ? raw.join(' ')
        : raw ?? (e instanceof Error ? e.message : null);
      setOfferPdfError(msg || 'Не удалось сформировать PDF');
    } finally {
      setOfferPdfLoading(false);
    }
  }, [queryClient, setValue]);

  const patch = async (body: SellerApplicationUpdate) => {
    const next = await sellerOnboardingApi.update(body);
    queryClient.setQueryData(SELLER_ME_QUERY_KEY, next);
    return next;
  };

  const goToStepWithSync = async (nextActive: number) => {
    const safeNext = Math.max(0, Math.min(LAST_STEP_INDEX, nextActive));
    if (safeNext === active) {
      return;
    }
    // При переходе назад (и в доработке — на любой шаг) синхронизируем currentStep в БД.
    const isRevision = Boolean(
      app &&
        sellerNeedsRevisionRegistrationWizard(
          app.status,
          app.moderationRejectionReason,
          app.moderationRejectionKind,
        ),
    );
    if (safeNext < active || isRevision) {
      try {
        await patch({ currentStep: safeNext + 1 });
      } catch {
        // Даже при ошибке сохранения даём пользователю остаться на нужном шаге локально.
      }
    }
    setActive(safeNext);
  };

  /** Сброс реквизитов компании (+ подсказок DaData). Инн сохраняем при переходе в самозанятого по продуктовой логике выше. */
  const clearCompanyStepFieldsExceptInn = () => {
    allowPartySuggestFetch.current = false;
    setPartySuggestions([]);
    setValue('kpp', '');
    setValue('ogrn', '');
    setValue('companyStatusText', '');
    setValue('companyName', '');
    setValue('legalAddress', '');
    setValue('directorFullName', '');
    setValue('egrulExtractFileUrls', []);
    setValue('companyLookupApplied', false);
  };

  const clearCompanyStepIncludingInn = () => {
    clearCompanyStepFieldsExceptInn();
    setValue('inn', '');
  };

  /** Смена типа на шаге 1 → очищаем несовместимые данные следующих шагов. */
  const onAccountTypeUserChange = async (
    prev: SellerAccountType | '',
    next: SellerAccountType,
  ) => {
    /** Серверное currentStep синхронизирует активный экран после PATCH; без этого старый шаг держался в БД → прыжок на шаг 2. */
    const currentStepSynced = Math.min(Math.max(active + 1, 1), STEP_COUNT);
    const wasBusiness =
      prev === 'LEGAL_ENTITY' || prev === 'INDIVIDUAL_ENTREPRENEUR';
    const becomesBusiness =
      next === 'LEGAL_ENTITY' || next === 'INDIVIDUAL_ENTREPRENEUR';
    const wasSelfEmployed = prev === 'SELF_EMPLOYED';
    /** Переключение только между юрлицом и ИП (не самозанятый): реквизиты из Дадаты несовместимы по смыслу */
    const switchLegalVersusIp =
      wasBusiness &&
      becomesBusiness &&
      prev !== next &&
      (prev === 'LEGAL_ENTITY' || prev === 'INDIVIDUAL_ENTREPRENEUR') &&
      (next === 'LEGAL_ENTITY' || next === 'INDIVIDUAL_ENTREPRENEUR');

    try {
      if (switchLegalVersusIp) {
        clearCompanyStepIncludingInn();
        await patch({
          accountType: next,
          currentStep: currentStepSynced,
          inn: '',
          kpp: '',
          ogrn: '',
          companyStatusText: '',
          companyName: '',
          legalAddress: '',
          directorFullName: '',
          egrulExtractFileUrls: [],
        });
      } else if (wasBusiness && next === 'SELF_EMPLOYED') {
        clearCompanyStepFieldsExceptInn();
        await patch({
          accountType: 'SELF_EMPLOYED',
          currentStep: currentStepSynced,
          kpp: '',
          ogrn: '',
          companyStatusText: '',
          companyName: '',
          legalAddress: '',
          directorFullName: '',
          egrulExtractFileUrls: [],
        });
      } else if (wasSelfEmployed && becomesBusiness) {
        clearCompanyStepIncludingInn();
        await patch({
          accountType: next,
          currentStep: currentStepSynced,
          inn: '',
          kpp: '',
          ogrn: '',
          companyStatusText: '',
          companyName: '',
          legalAddress: '',
          directorFullName: '',
          egrulExtractFileUrls: [],
        });
      }
    } catch {
      /** без тоста — пользователь сообщит при необходимости */
    }
  };

  const applyDaDataLookup = async (innDigits: string, lookupKpp?: string | null) => {
    const currentAccountType = getValues('accountType');
    const kppArg =
      lookupKpp != null && String(lookupKpp).trim() !== '' ? String(lookupKpp).trim() : undefined;
    setInnLookupLoading(true);
    try {
      const d = await sellerOnboardingApi.lookupInn(innDigits, kppArg ?? null);
      setValue('inn', d.inn);
      setValue('kpp', d.kpp ?? '');
      setValue('ogrn', d.ogrn);
      setValue('companyName', d.companyName);
      setValue('legalAddress', d.legalAddress);
      setValue('companyStatusText', d.companyStatusText);
      if (currentAccountType !== 'SELF_EMPLOYED') {
        setValue('companyLookupApplied', true);
      }
      if (d.directorFullName) {
        setValue('directorFullName', d.directorFullName);
      }
      if (currentAccountType !== 'SELF_EMPLOYED') {
        setValue('accountType', d.suggestedAccountType);
      }
      clearErrors(['inn', 'companyName']);
      await patch({
        /** Иначе после «Назад» БД держит старый currentStep и sync с GET/me снова перекидывает на следующий шаг */
        currentStep: Math.min(Math.max(active + 1, 1), STEP_COUNT),
        inn: d.inn,
        kpp: d.kpp ?? undefined,
        ogrn: d.ogrn,
        companyName: d.companyName,
        legalAddress: d.legalAddress,
        companyStatusText: d.companyStatusText,
        ...(d.directorFullName ? { directorFullName: d.directorFullName } : {}),
        ...(currentAccountType !== 'SELF_EMPLOYED' ? { accountType: d.suggestedAccountType } : {}),
      });
    } catch {
      /** ошибка запроса — без toast */
    } finally {
      setInnLookupLoading(false);
    }
  };

  const handlePartySuggestionPick = async (option: PartySuggestOption) => {
    allowPartySuggestFetch.current = false;
    const innDigits = (option.inn || '').replace(/\D/g, '');
    if (innDigits.length !== 10 && innDigits.length !== 12) {
      return;
    }
    await applyDaDataLookup(innDigits, option.kpp ?? undefined);
    setPartySuggestions([]);
  };

  const handleBankSuggestionPick = async (opt: BankSuggestOption) => {
    allowBankSuggestFetch.current = false;
    setValue('bik', opt.bik);
    setValue(
      'correspondentAccount',
      opt.correspondentAccount?.trim().length ? opt.correspondentAccount.trim() : '',
    );
    setValue('bankName', opt.bankName);
    setBankSuggestions([]);
    setBankSearchQuery((opt.bankName || opt.label).trim());
    try {
      await patch({
        currentStep: Math.min(Math.max(active + 1, 1), STEP_COUNT),
        bik: opt.bik,
        ...(opt.correspondentAccount?.trim()
          ? { correspondentAccount: opt.correspondentAccount.trim() }
          : { correspondentAccount: '' }),
        bankName: opt.bankName,
      });
    } catch {
      /** ошибка сохранения банка */
    }
  };

  const saveStepAndGo = async (nextActive: number) => {
    const v = getValues();
    const stepPatch: SellerApplicationUpdate = (() => {
      if (active === 0) {
        return {
          currentStep: nextActive + 1,
          ...(v.accountType ? { accountType: v.accountType } : { accountType: undefined }),
          ...(v.taxSystem ? { taxSystem: v.taxSystem } : { taxSystem: undefined }),
        };
      }
      return {
        currentStep: nextActive + 1,
        ...valuesToApiPatch(v),
      };
    })();

    if (active === 0) {
      clearErrors(['accountType', 'taxSystem']);
      if (!v.accountType) {
        setError('accountType', { type: 'manual', message: 'Выберите тип аккаунта' });
        return;
      }
      if (
        TAX_SYSTEM_SELECTION_ENABLED &&
        (v.accountType === 'LEGAL_ENTITY' || v.accountType === 'INDIVIDUAL_ENTREPRENEUR') &&
        !v.taxSystem
      ) {
        setError('taxSystem', {
          type: 'manual',
          message: 'Выберите систему налогообложения',
        });
        return;
      }
    }
    if (active === 1) {
      const innDigits = (v.inn || '').replace(/\D/g, '');
      const isSelfEmployed = v.accountType === 'SELF_EMPLOYED';

      clearErrors(['inn', 'companyName', 'legalAddress', 'directorFullName', 'contactEmail']);

      let stepHasError = false;

      if (isSelfEmployed) {
        if (innDigits.length !== 12) {
          setError('inn', { type: 'manual', message: 'Укажите ИНН физлица (12 цифр)' });
          stepHasError = true;
        }
        if (!optionalStr(v.companyName)) {
          setError('companyName', {
            type: 'manual',
            message: 'Укажите ФИО полностью',
          });
          stepHasError = true;
        }
      } else {
        if (innDigits.length !== 10 && innDigits.length !== 12) {
          setError('inn', {
            type: 'manual',
            message: 'Укажите корректный ИНН (10 или 12 цифр) или поисковую строку',
          });
          stepHasError = true;
        } else if (!v.companyLookupApplied) {
          setError('inn', {
            type: 'manual',
            message: 'Выберите организацию из списка подсказок DaData',
          });
          stepHasError = true;
        } else if (!optionalStr(v.companyName)) {
          setError('companyName', {
            type: 'manual',
            message: 'Подставьте данные — выберите организацию из подсказок',
          });
          stepHasError = true;
        }
      }

      if (!optionalStr(v.legalAddress)) {
        setError('legalAddress', {
          type: 'manual',
          message: 'Заполните адрес',
        });
        stepHasError = true;
      }

      if (!isSelfEmployed && v.companyLookupApplied) {
        const st = (v.companyStatusText || '').trim();
        if (st && st !== EGRUL_STATUS_ACTIVE_LABEL) {
          setError('inn', {
            type: 'manual',
            message: `По ЕГРЮЛ статус не «${EGRUL_STATUS_ACTIVE_LABEL}» (${st}). Регистрация недоступна.`,
          });
          stepHasError = true;
        }
      }

      if (!isSelfEmployed && !optionalStr(v.directorFullName)) {
        setError('directorFullName', {
          type: 'manual',
          message: 'Укажите ФИО генерального директора / ИП',
        });
        stepHasError = true;
      }

      const emailTrim = (v.contactEmail || '').trim();
      if (!emailTrim || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrim)) {
        setError('contactEmail', {
          type: 'manual',
          message: 'Укажите корректный email',
        });
        stepHasError = true;
      }

      if (stepHasError) {
        return;
      }
    }

    if (active === 2) {
      clearErrors(['bik', 'bankAccountNumber']);
      const bikDigits = (v.bik || '').replace(/\D/g, '');
      const rsDigits = (v.bankAccountNumber || '').replace(/\D/g, '');
      let stepHasError = false;
      if (bikDigits.length !== 9) {
        setError('bik', {
          type: 'manual',
          message: 'Выберите банк из списка подсказок',
        });
        stepHasError = true;
      }
      if (rsDigits.length !== 20) {
        setError('bankAccountNumber', {
          type: 'manual',
          message: 'Укажите расчётный счёт (20 цифр)',
        });
        stepHasError = true;
      } else if (isBankAccountChecksumValid(bikDigits, rsDigits) === false) {
        setError('bankAccountNumber', { type: 'manual', message: BANK_ACCOUNT_CHECKSUM_ERROR });
        stepHasError = true;
      }
      if (stepHasError) {
        return;
      }
    }

    try {
      await patch(stepPatch);
      clearErrors();
      setActive(nextActive);
    } catch (e: unknown) {
      const fieldErrors = extractPatchFieldErrors(e);
      const entries = Object.entries(fieldErrors) as Array<
        [keyof SellerRegisterFormValues, string]
      >;
      if (entries.length > 0) {
        for (const [field, message] of entries) {
          setError(field, { type: 'manual', message });
        }
        return;
      }
      const ax = e as { response?: { data?: { message?: string | string[] } } };
      const raw = ax.response?.data?.message;
      const message = Array.isArray(raw)
        ? raw.join(' ')
        : raw ?? (e instanceof Error ? e.message : 'Ошибка сохранения');
      setError('root.submit', { type: 'manual', message });
    }
  };

  const syncDocsFromApp = (next: SellerApplication) => {
    setValue('egrulExtractFileUrls', [...(next.egrulExtractFileUrls ?? [])]);
    setValue('passportScanFileUrls', [...(next.passportScanFileUrls ?? [])]);
    setValue('logoFileUrl', next.logoFileUrl ?? '');
  };

  const mergeDocumentsIntoCachedApp = (next: SellerApplication) => {
    queryClient.setQueryData<SellerApplication | undefined>(SELLER_ME_QUERY_KEY, (prev) => {
      if (!prev) return next;
      return {
        ...prev,
        egrulExtractFileUrls: [...(next.egrulExtractFileUrls ?? [])],
        passportScanFileUrls: [...(next.passportScanFileUrls ?? [])],
        logoFileUrl: next.logoFileUrl ?? '',
      };
    });
  };

  const uploadFiles = async (
    kind: 'egrul' | 'passport' | 'logo',
    files: FileList | null,
  ) => {
    if (!files?.length) return;
    setDocUploadKind(kind);
    try {
      let last: SellerApplication | undefined;
      const queue =
        kind === 'logo' && files[0]
          ? [files[0]]
          : Array.from(files);
      for (const file of queue) {
        last = await sellerOnboardingApi.uploadFile(kind, file);
        mergeDocumentsIntoCachedApp(last);
      }
      if (last) syncDocsFromApp(last);
    } catch {
      /** ошибка загрузки файла */
    } finally {
      setDocUploadKind(null);
    }
  };

  const removeFile = async (kind: 'egrul' | 'passport' | 'logo', url: string) => {
    try {
      const next = await sellerOnboardingApi.removeDocument(kind, url);
      mergeDocumentsIntoCachedApp(next);
      syncDocsFromApp(next);
    } catch {
      /** ошибка удаления */
    }
  };

  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
  const [logoutLoading, setLogoutLoading] = useState(false);

  const handleLogout = async () => {
    setLogoutLoading(true);
    authUtils.markManualLogout();
    try {
      await authApi.logout();
    } catch {
      // Даже при ошибке API очищаем локальную сессию и уводим на логин.
    }
    await clearSellerClientState(queryClient);
    authUtils.clearSellerSession();
    navigate('/login');
  };

  const onFinalSubmit = handleSubmit(async () => {
    if (!app) {
      return;
    }
    const v = getValues();
    clearErrors();
    if (!validateReadyForSubmit(v, app, setError)) {
      return;
    }
    setSubmitting(true);
    try {
      await patch({
        ...valuesToApiPatch(v),
        currentStep: STEP_COUNT,
      });
      await sellerOnboardingApi.submit();
      await queryClient.invalidateQueries({ queryKey: SELLER_ME_QUERY_KEY });
    } catch (e: unknown) {
      const fieldErrors = extractPatchFieldErrors(e);
      const entries = Object.entries(fieldErrors) as Array<
        [keyof SellerRegisterFormValues, string]
      >;
      if (entries.length > 0) {
        for (const [field, message] of entries) {
          setError(field, { type: 'manual', message });
        }
        return;
      }
      const message =
        e instanceof Error
          ? e.message
          : typeof e === 'object'
            ? String(e)
            : 'Ошибка отправки';
      setError('root.submit', { type: 'manual', message });
    } finally {
      setSubmitting(false);
    }
  });

  const taxStep0Options = useMemo(() => taxOptionsForAccountType(accountType), [accountType]);

  const egrulStatusBlocksContinue =
    isBusinessAccount &&
    companyLookupApplied &&
    (companyStatusText || '').trim() !== '' &&
    (companyStatusText || '').trim() !== EGRUL_STATUS_ACTIVE_LABEL;

  const docChooseButton = (kind: 'egrul' | 'passport' | 'logo') => {
    const loading = docUploadKind === kind;
    return (
      <span
        className={`button button--secondary seller-register-file-row-action${loading ? ' seller-register-file-row-action--loading' : ''}`}
        aria-hidden
      >
        {loading ? (
          <span className="seller-register-file-row-action-loading">
            <Loader size={14} />
            Загрузка…
          </span>
        ) : (
          'Выбрать'
        )}
      </span>
    );
  };

  if (isLoading || !app) {
    return (
      <div className="seller-register-page seller-register-page--loading">
        <Loader color="pink" />
      </div>
    );
  }

  const isRevisionWizard = sellerNeedsRevisionRegistrationWizard(
    app.status,
    app.moderationRejectionReason,
    app.moderationRejectionKind,
  );

  return (
    <div
      className={`seller-register-page${isRevisionWizard ? ' seller-register-page--embedded' : ''}`}
    >
      {!isRevisionWizard ? (
        <header className="seller-register-top">
          <div className="seller-register-top-brand">
            <img src={logoMark} alt="" className="seller-register-top-mark" />
            <div className="seller-register-top-text">
              <span className="seller-register-top-name">Лавка цветов</span>
              <span className="seller-register-top-sub">Кабинет продавца</span>
            </div>
          </div>
          <button
            type="button"
            className="seller-register-logout"
            onClick={() => setLogoutConfirmOpen(true)}
          >
            Выйти
          </button>
        </header>
      ) : null}

      <div className="seller-register-card">
        <div className="seller-register-card-head">
          {/* <p className="seller-register-eyebrow">Лавка цветов</p> */}
          <h1 className="seller-register-title">Регистрация продавца</h1>
          <p className="seller-register-lead">Заполните шаги — заявка уйдёт на модерацию</p>
        </div>

        <div className="seller-register-body">
          <Stepper
            className="seller-register-stepper"
            color="pink"
            active={active}
            onStepClick={(step) => {
              void goToStepWithSync(step);
            }}
            allowNextStepsSelect={isRevisionWizard}
            completedIcon={
              <svg
                className="seller-register-stepper__check"
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                aria-hidden
              >
                <path
                  d="M2.5 7.2L5.4 10.1L11.5 3.9"
                  stroke="currentColor"
                  strokeWidth="1.75"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            }
            styles={{
              separator: {
                flex: '0 0 1.25rem',
                marginInline: rem(8),
              },
            }}
          >
            <Stepper.Step label="Тип">
              <Stack gap="md" mt="md">
                <Controller
                  name="accountType"
                  control={control}
                  render={({ field }) => (
                    <Radio.Group
                      label="Тип аккаунта"
                      value={field.value}
                      onChange={(val) => {
                        const next = val as SellerAccountType;
                        const prev = field.value as SellerAccountType | '';
                        field.onChange(next);
                        void onAccountTypeUserChange(prev, next);
                      }}
                      onBlur={field.onBlur}
                    >
                      {(Object.keys(ACCOUNT_LABELS) as SellerAccountType[])
                        .filter(
                          (k) =>
                            k !== 'SELF_EMPLOYED' ||
                            SELF_EMPLOYED_REGISTRATION_ENABLED ||
                            field.value === 'SELF_EMPLOYED',
                        )
                        .map((k) => (
                          <Radio key={k} value={k} label={ACCOUNT_LABELS[k]} mt="xs" />
                        ))}
                    </Radio.Group>
                  )}
                />
                <div className='tax-system-container'>
                  {TAX_SYSTEM_SELECTION_ENABLED &&
                    (accountType === 'LEGAL_ENTITY' || accountType === 'INDIVIDUAL_ENTREPRENEUR') && (
                      <ControlledSelect
                        id="taxSystem"
                        name="taxSystem"
                        control={control}
                        label="Система налогообложения"
                        placeholder="Выберите"
                        data={taxStep0Options}
                        nothingFound="Нет вариантов"
                      />
                    )}
                </div>

                {accountType === 'SELF_EMPLOYED' && (
                  <>
                  </>
                )}
              </Stack>
            </Stepper.Step>
            <Stepper.Step label="Компания">
              <Stack gap="sm" mt="md">
                {accountType === 'SELF_EMPLOYED' ? (
                  <ControlledInput
                    id="inn"
                    name="inn"
                    control={control}
                    label="ИНН"
                    className="seller-register__inn-input"
                  />
                ) : (
                  <Stack gap="sm">
                    <Controller
                      name="inn"
                      control={control}
                      render={({ field, fieldState }) => (
                        <Input
                          id="inn"
                          label="ИНН, ОГРН или название для поиска"
                          className="seller-register__inn-input-wide"
                          value={field.value ?? ''}
                          onChange={(e) => {
                            allowPartySuggestFetch.current = true;
                            setValue('companyLookupApplied', false);
                            field.onChange(e.target.value);
                          }}
                          onBlur={field.onBlur}
                          error={fieldState.error?.message}
                        />
                      )}
                    />
                    {suggestLoading && (
                      <Text size="xs" c="dimmed">
                        Поиск в DaData…
                      </Text>
                    )}
                    {!suggestLoading &&
                      partySuggestions.length > 0 && (
                        <Paper
                          withBorder
                          radius="sm"
                          styles={{ root: { padding: rem(4) } }}
                        >
                          <ScrollArea.Autosize
                            mah={240}
                            styles={{
                              viewport: {
                                paddingInlineEnd: 0,
                                paddingBottom: 0,
                              },
                            }}
                          >
                            <Stack gap={0}>
                              {partySuggestions.map((opt) => (
                                <UnstyledButton
                                  key={opt.id}
                                  type="button"
                                  disabled={innLookupLoading}
                                  onClick={() => void handlePartySuggestionPick(opt)}
                                  style={{
                                    borderRadius: 6,
                                    textAlign: 'left',
                                    width: '100%',
                                    padding: `${rem(6)} ${rem(6)}`,
                                  }}
                                >
                                  <Text size="sm" lh={1.35}>
                                    {opt.label}
                                  </Text>
                                  <Text size="xs" c="dimmed">
                                    ИНН {opt.inn}
                                    {opt.kpp ? ` · КПП ${opt.kpp}` : ''}
                                  </Text>
                                </UnstyledButton>
                              ))}
                            </Stack>
                          </ScrollArea.Autosize>
                        </Paper>
                      )}
                    {!suggestLoading &&
                      partySuggestions.length === 0 &&
                      isPartySuggestQueryReady(debouncedInnSuggest) &&
                      allowPartySuggestFetch.current && (
                        <Text size="sm" c="dimmed">
                          Ничего не найдено. Проверьте ИНН, ОГРН или название и попробуйте снова.
                        </Text>
                      )}
                  </Stack>
                )}
                {egrulStatusBlocksContinue && (
                  <Alert tone="error" title="Статус в ЕГРЮЛ">
                    По данным DaData организация находится в статусе «{companyStatusText}». Дальнейшая регистрация на
                    маркетплейсе недоступна.
                  </Alert>
                )}
                {accountType !== 'SELF_EMPLOYED' && (
                  <Group grow align="flex-start" wrap="wrap">
                    <ControlledInput
                      id="ogrn"
                      name="ogrn"
                      control={control}
                      label="ОГРН / ОГРНИП"
                      disabled={isBusinessAccount}
                    />
                    <ControlledInput
                      id="kpp"
                      name="kpp"
                      control={control}
                      label="КПП"
                      disabled={accountType !== 'LEGAL_ENTITY'}
                    />

                  </Group>
                )}
                <ControlledInput
                  id="companyName"
                  name="companyName"
                  control={control}
                  label={accountType === 'SELF_EMPLOYED' ? 'ФИО полностью' : 'Название организации'}
                  disabled={isBusinessAccount}
                />
                <ControlledInput
                  id="legalAddress"
                  name="legalAddress"
                  control={control}
                  label={accountType === 'SELF_EMPLOYED' ? 'Адрес регистрации' : 'Юридический адрес'}
                />

                {accountType !== 'SELF_EMPLOYED' && (
                  <ControlledInput
                    id="directorFullName"
                    name="directorFullName"
                    control={control}
                    label="ФИО генерального директора / ИП"
                  />
                )}
                <Group grow align="flex-start" wrap="wrap">
                  <ControlledInput
                    id="contactPhone"
                    name="contactPhone"
                    control={control}
                    label="Телефон директора"
                    type="tel"
                    inputMode="tel"
                    placeholder="+7 (900) 000-00-00"
                    formatOnChange={formatPickupPhoneForInput}
                  />
                  <ControlledInput
                    id="contactEmail"
                    name="contactEmail"
                    control={control}
                    label="Email директора"
                    type="email"
                  />
                </Group>
              </Stack>
            </Stepper.Step>
            <Stepper.Step label="Реквизиты" >
              <Stack gap="sm" mt="md">
                <Stack gap="xs">
                  <Input
                    label="Название или бик банка для поиска"
                    placeholder="Например: Тинькофф, сбербанк, 044525225…"
                    value={bankSearchQuery}
                    onChange={(e) => {
                      allowBankSuggestFetch.current = true;
                      const next = e.target.value;
                      setBankSearchQuery(next);
                      setValue('bankName', next);
                    }}
                  />
                  {bankSuggestLoading && (
                    <Text size="xs" c="dimmed">
                      Поиск в DaData…
                    </Text>
                  )}
                  {bankSuggestions.length > 0 && (
                    <Paper withBorder radius="sm" styles={{ root: { padding: rem(4) } }}>
                      <ScrollArea.Autosize
                        mah={240}
                        styles={{
                          viewport: {
                            paddingInlineEnd: 0,
                            paddingBottom: 0,
                          },
                        }}
                      >
                        <Stack gap={0}>
                          {bankSuggestions.map((opt) => (
                            <UnstyledButton
                              key={opt.id}
                              type="button"
                              disabled={bankSuggestLoading}
                              onClick={() => void handleBankSuggestionPick(opt)}
                              style={{
                                borderRadius: 6,
                                textAlign: 'left',
                                width: '100%',
                                padding: `${rem(6)} ${rem(6)}`,
                              }}
                            >
                              <Text size="sm" lh={1.35}>
                                {opt.label}
                              </Text>
                              <Text size="xs" c="dimmed">
                                БИК {opt.bik}
                                {opt.correspondentAccount ? ` · к/с ${opt.correspondentAccount}` : ''}
                              </Text>
                            </UnstyledButton>
                          ))}
                        </Stack>
                      </ScrollArea.Autosize>
                    </Paper>
                  )}
                </Stack>
                <ControlledInput
                  id="correspondentAccount"
                  name="correspondentAccount"
                  control={control}
                  label="Корр. счёт"
                  disabled
                />
                <ControlledInput id="bik" name="bik" control={control} label="БИК" disabled />
                <ControlledInput
                  id="bankAccountNumber"
                  name="bankAccountNumber"
                  control={control}
                  label="Расчётный счёт"
                  inputMode="numeric"
                  placeholder="20 цифр"
                  maxLength={20}
                  formatOnChange={(value) => digitsOnlyMax(value, 20)}
                />
              </Stack>
            </Stepper.Step>
            <Stepper.Step label="Документы">
              <Stack gap="md" mt="md">
                {previousStepsSubmitMessages.length > 0 && (
                  <Alert tone="error" title="Проверьте данные на предыдущих шагах">
                    <Stack gap={4}>
                      {previousStepsSubmitMessages.map((msg, i) => (
                        <Text key={i} size="sm" c="inherit">
                          {msg}
                        </Text>
                      ))}
                    </Stack>
                  </Alert>
                )}
                <ControlledInput
                  id="shopName"
                  name="shopName"
                  control={control}
                  label="Название магазина"
                />
                <ControlledTextarea
                  id="activityDescription"
                  name="activityDescription"
                  control={control}
                  label="Описание деятельности"
                  minRows={3}
                />
                <Group align="flex-start" grow preventGrowOverflow={false} wrap="wrap">
                  <Stack gap="xs" style={{ minWidth: 200 }}>
                    <label
                      className={`seller-register-file-row${docUploadKind === 'logo' ? ' seller-register-file-row--loading' : ''}`}
                      title="Выбрать файл"
                      aria-label="Загрузить логотип магазина"
                    >
                      <input
                        type="file"
                        className="seller-register-file-row-input"
                        accept="image/*"
                        disabled={docUploadLocksInputs}
                        onChange={(e) => {
                          void uploadFiles('logo', e.target.files);
                          e.target.value = '';
                        }}
                      />
                      <Group
                        justify="flex-start"
                        align="center"
                        wrap="nowrap"
                        gap="xs"
                        className="seller-register-file-row-inner"
                      >
                        <Text size="sm" style={{ flex: '0 0 auto', minWidth: 0 }} fw={600}>
                          Логотип магазина
                        </Text>
                        {docChooseButton('logo')}
                      </Group>
                    </label>
                    <Text size="xs" c="dimmed">
                      Квадратный логотип 1:1, желательно 512×512 px (PNG или JPEG до 2 МБ). Важные
                      элементы размещайте в центре — по краям изображение обрезается.
                    </Text>
                    {errors.logoFileUrl?.message && (
                      <Text size="xs" c="red" role="alert">
                        {errors.logoFileUrl.message}
                      </Text>
                    )}
                    {logoFileUrl.trim() ? (
                      <Box pos="relative" display="inline-block" className="seller-register-logo-wrap">
                        <img
                          src={devProxyS3Url(logoFileUrl) || logoFileUrl}
                          alt="Логотип магазина"
                          className="seller-register-logo-preview"
                        />

                      </Box>
                    ) : null}
                  </Stack>

                </Group>
                {accountType !== 'SELF_EMPLOYED' && (
                  <Stack gap="xs">
                    <label
                      className={`seller-register-file-row${docUploadKind === 'egrul' ? ' seller-register-file-row--loading' : ''}`}
                      title="Добавить файлы"
                      aria-label="Добавить файлы выписки ЕГРЮЛ / ЕГРИП"
                    >
                      <input
                        type="file"
                        className="seller-register-file-row-input"
                        multiple
                        accept=".pdf,image/*"
                        disabled={docUploadLocksInputs}
                        onChange={(e) => {
                          void uploadFiles('egrul', e.target.files);
                          e.target.value = '';
                        }}
                      />
                      <Group
                        justify="flex-start"
                        align="center"
                        wrap="nowrap"
                        gap="xs"
                        className="seller-register-file-row-inner"
                      >
                        <Text size="sm" style={{ flex: '0 0 auto', minWidth: 0 }} fw={600}>
                          Выписка ЕГРЮЛ / ЕГРИП (не старше 30 дней)
                        </Text>
                        {docChooseButton('egrul')}
                      </Group>
                    </label>
                    {errors.egrulExtractFileUrls?.message && (
                      <Text size="xs" c="red" role="alert">
                        {errors.egrulExtractFileUrls.message}
                      </Text>
                    )}
                    <Stack gap={4}>
                      {egrulExtractFileUrls.map((url) => (
                        <Group
                          key={url}
                          gap="xs"
                          wrap="nowrap"
                          justify="flex-start"
                          align="center"
                          className="seller-register-file-list-item"
                        >
                          <Text
                            size="xs"
                            c="dimmed"
                            className="seller-register-file-list-item__name"
                            truncate
                          >
                            <a
                              className="seller-register-doc-link"
                              href="#"
                              onClick={(event) => {
                                event.preventDefault();
                                openSellerDocument(url);
                              }}
                            >
                              {fileLabelFromUrl(url)}
                            </a>
                          </Text>
                          <ActionIcon
                            type="button"
                            size="sm"
                            variant="subtle"
                            color="gray"
                            className="seller-register-file-list-item__remove"
                            aria-label="Удалить файл"
                            onClick={() => void removeFile('egrul', url)}
                          >
                            <IoClose size={18} />
                          </ActionIcon>
                        </Group>
                      ))}
                    </Stack>
                  </Stack>
                )}
                <Stack gap="xs">
                  <label
                    className={`seller-register-file-row${docUploadKind === 'passport' ? ' seller-register-file-row--loading' : ''}`}
                    title="Добавить файлы"
                    aria-label="Добавить файлы скана паспорта"
                  >
                    <input
                      type="file"
                      className="seller-register-file-row-input"
                      multiple
                      accept="image/*,.pdf"
                      disabled={docUploadLocksInputs}
                      onChange={(e) => {
                        void uploadFiles('passport', e.target.files);
                        e.target.value = '';
                      }}
                    />
                    <Group
                      justify="flex-start"
                      align="center"
                      wrap="nowrap"
                      gap="xs"
                      className="seller-register-file-row-inner"
                    >
                      <Text size="sm" style={{ flex: '0 0 auto', minWidth: 0 }} fw={600}>
                        {accountType === 'SELF_EMPLOYED'
                          ? 'Скан вашего паспорта'
                          : 'Скан паспорта руководителя'}
                      </Text>
                      {docChooseButton('passport')}
                    </Group>
                  </label>
                  {errors.passportScanFileUrls?.message && (
                    <Text size="xs" c="red" role="alert">
                      {errors.passportScanFileUrls.message}
                    </Text>
                  )}
                  <Stack gap={4}>
                    {passportScanFileUrls.map((url) => (
                      <Group
                        key={url}
                        gap="xs"
                        wrap="nowrap"
                        justify="flex-start"
                        align="center"
                        className="seller-register-file-list-item"
                      >
                        <Text
                          size="xs"
                          c="dimmed"
                          className="seller-register-file-list-item__name"
                          truncate
                        >
                          <a
                            className="seller-register-doc-link"
                            href="#"
                            onClick={(event) => {
                              event.preventDefault();
                              openSellerDocument(url);
                            }}
                          >
                            {fileLabelFromUrl(url)}
                          </a>
                        </Text>
                        <ActionIcon
                          type="button"
                          size="sm"
                          variant="subtle"
                          color="gray"
                          className="seller-register-file-list-item__remove"
                          aria-label="Удалить файл"
                          onClick={() => void removeFile('passport', url)}
                        >
                          <IoClose size={18} />
                        </ActionIcon>
                      </Group>
                    ))}
                  </Stack>
                </Stack>


                <Stack gap="md" className="seller-register-offer" mt="lg">
                  <Text fw={600} size="sm">
                    Договор-оферта
                  </Text>
                  {offerPdfLoading && (
                    <Group gap="xs">
                      <Loader size="sm" />
                      <Text size="sm">Формируем PDF…</Text>
                    </Group>
                  )}
                  {offerPdfError && (
                    <Alert tone="error" title="Не удалось сформировать PDF">
                      <Stack gap="sm">
                        <Text size="sm" c="inherit">{offerPdfError}</Text>
                        <Button type="button" variant="secondary" size="xs" onClick={() => void regenerateOfferPdf()}>
                          Повторить
                        </Button>
                      </Stack>
                    </Alert>
                  )}
                  {!offerPdfLoading && app.offerPdfFileUrl && (
                    <button
                      type="button"
                      className="seller-register-offer-link"
                      onClick={() => openSellerDocument(app.offerPdfFileUrl!)}
                    >
                      <span className="seller-register-offer-link__icon" aria-hidden>
                        PDF
                      </span>
                      <span className="seller-register-offer-link__text">
                        Открыть договор-оферту
                      </span>
                      <span className="seller-register-offer-link__hint">в новой вкладке</span>
                    </button>
                  )}
                  {errors.offerPdfSubmitError?.message && (
                    <Text size="xs" c="red" role="alert">
                      {errors.offerPdfSubmitError.message}
                    </Text>
                  )}
                  <ControlledCheckbox
                    name="offerAccepted"
                    control={control}
                    label="Ознакомился с текстом договора-оферты и согласен с условиями"
                  />
                </Stack>
              </Stack>
            </Stepper.Step>
          </Stepper>
        </div>

        <Stack gap="xs" className="seller-register-footer">
          {submitApiError?.message && (
            <Alert tone="error">{submitApiError.message}</Alert>
          )}
          <Group justify="space-between" w="100%">
            {active === 0 ? <div /> : <Button type="button" variant="secondary" disabled={active === 0} onClick={() => {
              void goToStepWithSync(active - 1);
            }}>
              Назад
            </Button>}
            {active < LAST_STEP_INDEX ? (
              <Button
                type="button"
                disabled={active === 1 && egrulStatusBlocksContinue}
                onClick={() => saveStepAndGo(active + 1)}
              >
                Далее
              </Button>
            ) : (
              <Button
                type="button"
                loading={submitting}
                disabled={
                  offerPdfLoading || submitting || Boolean(offerPdfError) || !offerAccepted
                }
                onClick={onFinalSubmit}
              >
                Отправить на модерацию
              </Button>
            )}
          </Group>
        </Stack>
      </div>
      {!isRevisionWizard ? (
        <DoubleCheckModal
          isOpen={logoutConfirmOpen}
          onClose={() => {
            if (!logoutLoading) setLogoutConfirmOpen(false);
          }}
          title="Выйти из регистрации?"
          message="Сессия завершится, и потребуется снова войти по номеру телефона. Черновик заявки сохранится."
          firstButtonText="Отмена"
          secondButtonText="Выйти"
          secondButtonAction={() => {
            void handleLogout();
          }}
          isLoading={logoutLoading}
        />
      ) : null}
    </div>
  );
}
